import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { useTapOnce } from '../ui/useTapOnce';
import { sendOnce } from '../tickets/sendOnce';

/**
 * Whether a draft's line goes back to the job: a part used on it, a charge added to it (12c), or
 * its time (13), never what came off one (it goes with its line).
 */
export const goesBackToJob = (line: {
  jobCostId: string | null;
  jobChargeId: string | null;
  timeEntryId?: string | null;
  adjustsLineId: string | null;
}): boolean =>
  Boolean(line.jobCostId || line.jobChargeId || line.timeEntryId) && !line.adjustsLineId;

/**
 * Back to job (Inventory v2 12b, P10): a draft is never edited by hand, so a part line goes back
 * to the job, where it is left to bill again, and the office bills it again from the bill sheet.
 * What came off it goes with it. One send per tap (`useTapOnce`).
 */
export function BackToJobButton({ lineId, name }: { lineId: string; name: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const lock = useTapOnce();
  const back = useMutation(
    trpc.invoice.lineBackToJob.mutationOptions({
      onSettled: () => lock.release(),
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );
  return (
    <span className="inline-flex items-center gap-2 text-xs font-normal">
      <button
        type="button"
        className="font-semibold text-[var(--sw-brand-text)] hover:underline disabled:opacity-50"
        aria-label={`Send ${name} back to the job`}
        disabled={back.isPending}
        onClick={() => sendOnce(lock, back.mutate, { lineId })}
      >
        {back.isPending ? 'Sending back…' : 'Back to job'}
      </button>
      {back.error ? (
        <span className="text-[var(--sw-danger-text)]">{back.error.message}</span>
      ) : null}
    </span>
  );
}
